import { Links, Meta, Pages } from '../ApiModel';
import { hasValue, range } from './ArrayHelper';

export type PagedItem<T> = {
    items: T[];
    total: number;
    pageLinks?: Pages;
};

export type PageSpec = string | PageRequest | undefined;

// We want to load as many items per page as possible during loadAllItems operations
const itemsPerPageForLoadAll = 200;

type PageRequest = { page: number; perPage: number };

function isPageLink(link: PageSpec): link is string {
    return typeof link === 'string';
}

export function asPagedItems<T>(items: T[], response: { meta?: Meta; links?: Links | null }): PagedItem<T> {
    return {
        items: items,
        total: response.meta?.total || items.length,
        pageLinks: response.links?.pages,
    };
}

function pageRequestAsQueryParams(pageRequest: PageRequest): string {
    return `page=${pageRequest.page}&per_page=${pageRequest.perPage}`;
}

export function pageSpecAsUrl(pageSpec: PageSpec | undefined, baseUrl: string): string {
    if (!pageSpec) return baseUrl; // default to baseUrl with server defaults for pagination

    if (isPageLink(pageSpec)) return pageSpec; // We have a url already

    return baseUrl + (baseUrl.includes('?') ? '&' : '?') + pageRequestAsQueryParams(pageSpec);
}

export async function forEachPage<T>(
    loader: (link?: PageSpec) => Promise<PagedItem<T>>,
    onPage?: (page: PagedItem<T>) => void,
    numPerPage?: number
): Promise<void> {
    let page = await loader(numPerPage ? { page: 1, perPage: numPerPage } : undefined);
    onPage?.(page);
    while (page.pageLinks?.next) {
        page = await loader(page.pageLinks.next);
        onPage?.(page);
    }
}

export async function loadAllItems<T>(
    loader: (link?: PageSpec) => Promise<PagedItem<T>>,
    onItems?: (items: T[]) => void
): Promise<T[]> {
    let items: T[] = [];
    await forEachPage(
        loader,
        (page) => {
            items = items.concat(page.items);
            onItems?.(page.items);
        },
        itemsPerPageForLoadAll
    );
    return items;
}

// split the pages into ranges including 2 start pages, 2 end pages, and a page either side of the current page (if possible)
export function pageButtonRanges(currentPage: number, pageCount: number): number[][] {
    const firstPage = 1;
    const lastPage = pageCount;
    const firstSectionEnd = firstPage + 1;
    const middleSectionStart = Math.min(currentPage - 2, lastPage - 6);
    const middleSectionEnd = Math.max(currentPage + 2, 7);
    const lastSectionStart = lastPage - 1;

    const pages: number[] = [];
    for (const page of range(firstPage, lastPage)) {
        if (pageCount <= 9) {
            pages.push(page);
        } else if (page <= firstSectionEnd) {
            pages.push(page);
        } else if (page >= middleSectionStart && page <= middleSectionEnd) {
            pages.push(page);
        } else if (page >= lastSectionStart) {
            pages.push(page);
        }
    }

    if (pages.length < 1) return [];
    if (pages.length < 2) return [[firstPage]];

    const ranges: number[][] = [];
    let currentRange: number[] = [firstPage];
    for (let i = 1; i < pages.length; i++) {
        const prevPage = pages[i - 1];
        const currentPage = pages[i];
        if (!hasValue(currentPage) || !hasValue(prevPage)) continue;
        if (currentPage - prevPage > 1) {
            ranges.push(currentRange);
            currentRange = [currentPage];
        } else {
            if (currentPage) currentRange.push(currentPage);
        }
    }
    ranges.push(currentRange);
    return ranges;
}
